import axios, { AxiosResponse } from "axios";
import apiInstance from "api/axios";
import { BASE_URL, Requests } from "config/api";
import {
  AdminImporterPriceType,
  CarMakeType,
  DealerInventoryLeadType,
  DealerInventoryType,
  DealerLeadStatusType,
  DealerMemberRoleType,
  DealerMemberType,
  DealerSubmissionStatusType,
  DealerSubmissionType,
  DealerType,
  ImporterModelType,
  MasterCarProfileType,
} from "types/loadedData";

const replaceId = (route: string, id: string) => route.replace(":id", id);

const dealerApi = axios.create({ baseURL: BASE_URL });

dealerApi.interceptors.request.use((config: any) => {
  const token = localStorage.getItem("dealerToken");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export type DealerInventoryPayload = {
  carMake?: string;
  importerModel?: string;
  customModel?: string;
  make: string;
  model: string;
  year: string | number;
  color: string;
  fuelType: string;
  kmRange: string;
  batteryRangeKm?: string;
  price: string | number;
  availabilityStatus?: string;
};

export type DealerInventorySearchResult = {
  inventory: DealerInventoryType[];
  suggestions: string[];
};

export type DealerSubmissionPayload = {
  dealerId?: string;
  makeModelText?: string;
  year?: string | number;
  fuelType?: string;
  color?: string;
  batteryRangeKm?: string | number;
  price?: string | number;
  isDeal?: boolean;
  note?: string;
  status?: DealerSubmissionStatusType;
  staffNotes?: string;
  make?: string;
  model?: string;
};

export type DealerSubmissionPublishPayload = {
  make: string;
  model: string;
  year: string | number;
  price: string | number;
  fuelType: string;
  color: string;
  batteryRangeKm?: string | number;
  isDeal?: boolean;
  description?: string;
};

export const loginDealer = async (payload: {
  phoneNumber: string;
  password: string;
}): Promise<{ dealer: DealerType; token: string }> => {
  const response: AxiosResponse = await dealerApi.post(
    Requests.post.loginDealer,
    payload
  );
  return response.data;
};

export const getDealerInventory = async (): Promise<DealerInventoryType[]> => {
  const response: AxiosResponse = await dealerApi.get(
    Requests.get.getDealerInventory
  );
  return response.data?.inventory || [];
};

export const createDealerInventory = async (
  payload: DealerInventoryPayload
): Promise<DealerInventoryType> => {
  const response: AxiosResponse = await dealerApi.post(
    Requests.post.createDealerInventory,
    payload
  );
  return response.data?.inventory;
};

export const updateDealerInventory = async (
  id: string,
  payload: DealerInventoryPayload
): Promise<DealerInventoryType> => {
  const response: AxiosResponse = await dealerApi.patch(
    replaceId(Requests.update.updateDealerInventory, id),
    payload
  );
  return response.data?.inventory;
};

export const confirmDealerInventory = async (
  id: string
): Promise<DealerInventoryType> => {
  const response: AxiosResponse = await dealerApi.patch(
    replaceId(Requests.update.confirmDealerInventory, id)
  );
  return response.data?.inventory;
};

export const deleteDealerInventory = async (id: string): Promise<void> => {
  await dealerApi.delete(replaceId(Requests.delete.deleteDealerInventory, id));
};

export const getDealerSubmissions = async (
  dealerId?: string
): Promise<DealerSubmissionType[]> => {
  const response: AxiosResponse = await dealerApi.get(
    Requests.get.getDealerSubmissions,
    { params: dealerId ? { dealerId } : undefined }
  );
  return response.data?.submissions || [];
};

export const getPortalDealerAccounts = async (
  search = ""
): Promise<DealerType[]> => {
  const response: AxiosResponse = await dealerApi.get(
    Requests.get.getPortalDealerAccounts,
    { params: search.trim() ? { search: search.trim() } : undefined }
  );
  return response.data?.dealers || [];
};

export const createDealerSubmission = async (
  payload: DealerSubmissionPayload
): Promise<DealerSubmissionType> => {
  const response: AxiosResponse = await dealerApi.post(
    Requests.post.createDealerSubmission,
    payload
  );
  return response.data?.submission;
};

export const updateDealerSubmission = async (
  id: string,
  payload: DealerSubmissionPayload
): Promise<DealerSubmissionType> => {
  const response: AxiosResponse = await dealerApi.patch(
    replaceId(Requests.update.updateDealerSubmission, id),
    payload
  );
  return response.data?.submission;
};

export const uploadDealerSubmissionPhoto = async (
  id: string,
  slot: string,
  image: File,
  order?: number
): Promise<void> => {
  const formData = new FormData();
  formData.append("slot", slot);
  if (order !== undefined) formData.append("order", String(order));
  formData.append("image", image);
  await dealerApi.post(
    replaceId(Requests.post.uploadDealerSubmissionPhoto, id),
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
};

export const deleteDealerSubmission = async (id: string): Promise<void> => {
  await dealerApi.delete(replaceId(Requests.delete.deleteDealerSubmission, id));
};

export const deleteDealerSubmissionPhoto = async ({
  id,
  imageId,
}: {
  id: string;
  imageId: string;
}): Promise<void> => {
  await dealerApi.delete(
    Requests.delete.deleteDealerSubmissionPhoto
      .replace(":id", id)
      .replace(":imageId", imageId)
  );
};

export const getCarMakes = async (): Promise<CarMakeType[]> => {
  const response: AxiosResponse = await apiInstance.get(Requests.get.getCarMakes);
  return response.data?.carMakes || [];
};

export const getImporterModels = async ({
  make,
  search,
}: {
  make?: string;
  search?: string;
} = {}): Promise<ImporterModelType[]> => {
  const response: AxiosResponse = await apiInstance.get(
    Requests.get.getImporterModels,
    { params: { make, search } }
  );
  return response.data?.models || [];
};

export const getAdminImporterModels = async (
  search?: string
): Promise<ImporterModelType[]> => {
  const response: AxiosResponse = await apiInstance.get(
    Requests.get.getAdminImporterModels,
    { params: search ? { search } : undefined }
  );
  return response.data?.models || [];
};

export const createImporterModel = async (payload: {
  make: string;
  name: string;
  aliases?: string[];
  reviewStatus?: "Pending" | "Approved" | "Rejected";
  isActive?: boolean;
}): Promise<ImporterModelType> => {
  const response: AxiosResponse = await apiInstance.post(
    Requests.post.createImporterModel,
    payload
  );
  return response.data?.model;
};

export const updateImporterModel = async (
  id: string,
  payload: Partial<ImporterModelType>
): Promise<ImporterModelType> => {
  const response: AxiosResponse = await apiInstance.patch(
    replaceId(Requests.update.updateImporterModel, id),
    payload
  );
  return response.data?.model;
};

export const getMasterProfiles = async ({
  make,
  search,
}: {
  make?: string;
  search?: string;
} = {}): Promise<MasterCarProfileType[]> => {
  const response: AxiosResponse = await apiInstance.get(
    Requests.get.getMasterProfiles,
    { params: { make, search } }
  );
  return response.data?.profiles || [];
};

export const getAdminMasterProfiles = async (
  search?: string
): Promise<MasterCarProfileType[]> => {
  const response: AxiosResponse = await apiInstance.get(
    Requests.get.getAdminMasterProfiles,
    { params: search ? { search } : undefined }
  );
  return response.data?.profiles || [];
};

export const createMasterProfile = async (payload: {
  make: string;
  model: string;
  year: string | number;
  fuelType: string;
  color: string;
  batteryRangeKm?: string | number;
  status?: MasterCarProfileType["status"];
}): Promise<MasterCarProfileType> => {
  const response: AxiosResponse = await apiInstance.post(
    Requests.post.createMasterProfile,
    payload
  );
  return response.data?.profile;
};

export const updateMasterProfile = async (
  id: string,
  payload: Partial<MasterCarProfileType>
): Promise<MasterCarProfileType> => {
  const response: AxiosResponse = await apiInstance.patch(
    replaceId(Requests.update.updateMasterProfile, id),
    payload
  );
  return response.data?.profile;
};

export const getDealers = async (search?: string): Promise<DealerType[]> => {
  const response: AxiosResponse = await apiInstance.get(Requests.get.getDealers, {
    params: search ? { search } : undefined,
  });
  return response.data?.dealers || [];
};

export const createDealer = async (payload: {
  name: string;
  phoneNumber: string;
  email?: string;
  password: string;
  notes?: string;
  trustedDealer: boolean;
  isActive: boolean;
  portalRole?: "Importer" | "Admin";
}): Promise<DealerType> => {
  const response: AxiosResponse = await apiInstance.post(
    Requests.post.createDealer,
    payload
  );
  return response.data?.dealer;
};

export const updateDealer = async (
  id: string,
  payload: Partial<DealerType> & { password?: string }
): Promise<DealerType> => {
  const response: AxiosResponse = await apiInstance.patch(
    replaceId(Requests.update.updateDealer, id),
    payload
  );
  return response.data?.dealer;
};

export const getDealerMembers = async (
  search?: string,
  dealer?: string
): Promise<DealerMemberType[]> => {
  const response: AxiosResponse = await apiInstance.get(
    Requests.get.getDealerMembers,
    {
      params: {
        ...(search ? { search } : {}),
        ...(dealer ? { dealer } : {}),
      },
    }
  );
  return response.data?.members || [];
};

export const createDealerMember = async (payload: {
  dealer: string;
  userPhone: string;
  role: DealerMemberRoleType;
  canDirectPost: boolean;
  isActive: boolean;
}): Promise<DealerMemberType> => {
  const response: AxiosResponse = await apiInstance.post(
    Requests.post.createDealerMember,
    payload
  );
  return response.data?.member;
};

export const updateDealerMember = async (
  id: string,
  payload: Partial<Pick<DealerMemberType, "role" | "canDirectPost" | "isActive">>
): Promise<DealerMemberType> => {
  const response: AxiosResponse = await apiInstance.patch(
    replaceId(Requests.update.updateDealerMember, id),
    payload
  );
  return response.data?.member;
};

export const deleteDealerMember = async (id: string): Promise<void> => {
  await apiInstance.delete(replaceId(Requests.delete.deleteDealerMember, id));
};

export const getAdminDealerInventory = async (
  approvalStatus?: string,
  search?: string
): Promise<DealerInventorySearchResult> => {
  const response: AxiosResponse = await apiInstance.get(
    Requests.get.getAdminDealerInventory,
    {
      params: {
        ...(approvalStatus ? { approvalStatus } : {}),
        ...(search ? { search } : {}),
      },
    }
  );
  return {
    inventory: response.data?.inventory || [],
    suggestions: response.data?.suggestions || [],
  };
};

export const updateAdminDealerInventory = async (
  id: string,
  payload: Partial<DealerInventoryType> & { flagSuspicious?: boolean }
): Promise<DealerInventoryType> => {
  const response: AxiosResponse = await apiInstance.patch(
    replaceId(Requests.update.updateAdminDealerInventory, id),
    payload
  );
  return response.data?.inventory;
};

export const getAdminDealerSubmissions = async (
  search?: string,
  status?: string
): Promise<{ submissions: DealerSubmissionType[]; count: number }> => {
  const response: AxiosResponse = await apiInstance.get(
    Requests.get.getAdminDealerSubmissions,
    {
      params: {
        ...(search ? { search } : {}),
        ...(status && status !== "All" ? { status } : {}),
      },
    }
  );
  return {
    submissions: response.data?.submissions || [],
    count: response.data?.count || 0,
  };
};

export const updateAdminDealerSubmission = async (
  id: string,
  payload: DealerSubmissionPayload
): Promise<DealerSubmissionType> => {
  const response: AxiosResponse = await apiInstance.patch(
    replaceId(Requests.update.updateAdminDealerSubmission, id),
    payload
  );
  return response.data?.submission;
};

export const publishDealerSubmission = async (
  id: string,
  payload: DealerSubmissionPublishPayload
): Promise<DealerSubmissionType> => {
  const response: AxiosResponse = await apiInstance.post(
    replaceId(Requests.post.publishDealerSubmission, id),
    payload
  );
  return response.data?.submission;
};

export const publishOwnDealerSubmission = async (
  id: string,
  payload: DealerSubmissionPublishPayload
): Promise<DealerSubmissionType> => {
  const response: AxiosResponse = await dealerApi.post(
    replaceId(Requests.post.publishOwnDealerSubmission, id),
    payload
  );
  return response.data?.submission;
};

export const getDealerLeads = async (
  search?: string
): Promise<DealerInventoryLeadType[]> => {
  const response: AxiosResponse = await apiInstance.get(
    Requests.get.getDealerLeads,
    { params: search ? { search } : undefined }
  );
  return response.data?.leads || [];
};

export const updateDealerLead = async (
  id: string,
  payload: {
    status?: DealerLeadStatusType;
    notes?: string;
    commissionOutcome?: string;
  }
): Promise<DealerInventoryLeadType> => {
  const response: AxiosResponse = await apiInstance.patch(
    replaceId(Requests.update.updateDealerLead, id),
    payload
  );
  return response.data?.lead;
};

export const getAdminImporterPrices = async (
  search?: string
): Promise<AdminImporterPriceType[]> => {
  const response: AxiosResponse = await apiInstance.get(
    Requests.get.getAdminImporterPrices,
    { params: search ? { search } : undefined }
  );
  return response.data?.prices || [];
};

export const updateAdminImporterPrice = async (
  id: string,
  payload: {
    action: "confirm" | "update" | "unavailable" | "available";
    price?: number;
    expectedLastConfirmedAt?: string;
  }
): Promise<AdminImporterPriceType> => {
  const response: AxiosResponse = await apiInstance.patch(
    replaceId(Requests.update.updateAdminImporterPrice, id),
    payload
  );
  return response.data?.price;
};
